import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Zap, CheckCircle2, Clock, Coins, Search, Filter } from 'lucide-react';
import OceanBackground from '@/components/background/OceanBackground';

const MOCK_TRACES = [
  {
    id: 'trace-001', time: '9月15日 14:32', query: '带父母去杭州 4天 预算5000',
    duration: 2340, tokens: 1842, candidates: 24, filtered: 9, rate: '62.5%',
    nodes: [
      { step: '意图提取', detail: '杭州 · 4天3晚 · 3人（父母） · 预算 ≤5000', time: 180, status: 'success' },
      { step: '候选发现', detail: '24 家候选酒店召回', time: 45, status: 'success' },
      { step: '硬约束过滤', detail: '24 家 → 9 家通过', time: 320, status: 'success' },
      { step: '多目标排序', detail: '位置、交通、舒适度、预算四维加权', time: 890, status: 'success' },
      { step: '方案输出', detail: '最终推荐 Top 3 方案', time: 210, status: 'success' },
    ]
  },
  {
    id: 'trace-002', time: '9月14日 10:15', query: '情侣去东京 拍照 购物',
    duration: 1890, tokens: 1520, candidates: 32, filtered: 11, rate: '65.6%',
    nodes: [
      { step: '意图提取', detail: '东京 · 5天4晚 · 2人（情侣） · 偏好拍照+购物', time: 160, status: 'success' },
      { step: '候选发现', detail: '32 家候选酒店召回', time: 38, status: 'success' },
      { step: '硬约束过滤', detail: '32 家 → 11 家通过', time: 280, status: 'success' },
      { step: '多目标排序', detail: '位置、交通、舒适度、预算四维加权', time: 720, status: 'success' },
      { step: '方案输出', detail: '最终推荐 Top 3 方案', time: 190, status: 'success' },
    ]
  }
];

const DecisionTrace = () => {
  const [expanded, setExpanded] = useState(0);
  const trace = MOCK_TRACES[expanded];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-20 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.94)', letterSpacing: '-0.025em' }}>决策追踪</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>看懂 AI 为什么推荐这一方案</p>
        </div>

        {/* Trace Selector */}
        <div className="flex gap-3 mb-8 flex-wrap">
          {MOCK_TRACES.map((t, i) => (
            <button key={t.id} onClick={() => setExpanded(i)}
              className={`px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                i === expanded ? '' : 'ocean-glass'
              }`}
              style={i === expanded ? { background: 'linear-gradient(135deg, #4AA7D8, #3d82b0)', color: 'white' } : { color: 'rgba(255,255,255,0.6)' }}>
              <span className="font-medium">{t.query}</span>
              <span className={`text-xs ml-2 ${i === expanded ? 'opacity-70' : ''}`} style={i !== expanded ? { color: 'rgba(255,255,255,0.35)' } : {}}>{t.time}</span>
            </button>
          ))}
        </div>

        {/* Metrics Row (降级为辅助信息) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: '总耗时', value: `${(trace.duration / 1000).toFixed(1)}s`, icon: Clock },
            { label: 'Token 消耗', value: trace.tokens.toLocaleString(), icon: Coins },
            { label: '候选总数', value: trace.candidates, icon: Search },
            { label: '过滤率', value: trace.rate, icon: Filter },
          ].map((m, i) => (
            <div key={i} className="ocean-glass p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <m.icon className="w-3.5 h-3.5" style={{ color: '#4AA7D8' }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{m.label}</span>
              </div>
              <p className="text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Decision Timeline */}
        <div className="ocean-glass p-6">
          <div className="flex items-center gap-2 mb-6">
            <Zap className="w-4 h-4" style={{ color: '#D8B15A' }} />
            <h2 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>决策链路</h2>
          </div>
          <div className="space-y-0">
            {trace.nodes.map((node, i) => (
              <div key={i} className="flex gap-4">
                {/* Timeline marker */}
                <div className="flex flex-col items-center">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(57,201,130,0.15)' }}>
                    <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#39C982' }} />
                  </div>
                  {i < trace.nodes.length - 1 && (
                    <div className="w-px flex-1 my-1" style={{ background: 'rgba(57,201,130,0.2)' }} />
                  )}
                </div>
                {/* Content */}
                <div className="flex-1 pb-5">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.9)' }}>{node.step}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(74,167,216,0.1)', color: 'rgba(74,167,216,0.7)' }}>{node.time}ms</span>
                  </div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{node.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionTrace;
